import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth';
import type { Organization } from './types';

interface OrgState {
  orgs: Organization[];
  currentOrg: Organization | null;
  loading: boolean;
  isPlatformAdmin: boolean;
  refresh: () => Promise<void>;
  setCurrent: (id: string) => void;
  createOrg: (input: Partial<Organization> & { name: string }) => Promise<{ error: string | null; org: Organization | null }>;
}

const OrgContext = createContext<OrgState | null>(null);
const STORAGE_KEY = 'dodobooks-current-org';

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY)
  );
  const [loading, setLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // Load the platform-admin flag whenever the auth user changes.
  useEffect(() => {
    if (!user) { setIsPlatformAdmin(false); return; }
    supabase.from('dodobooks_user_profiles').select('is_platform_admin').eq('user_id', user.id).maybeSingle()
      .then(({ data }) => setIsPlatformAdmin(!!data?.is_platform_admin));
  }, [user]);

  const refresh = useCallback(async () => {
    if (!user) {
      setOrgs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    // RLS only returns orgs the user is a member of.
    const { data, error } = await supabase
      .from('dodobooks_organizations')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: true });
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load orgs', error);
      setOrgs([]);
    } else {
      setOrgs((data ?? []) as Organization[]);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Keep a sensible currentId — first org if none selected, clear if user logged out.
  useEffect(() => {
    if (!user) { setCurrentId(null); return; }
    if (orgs.length === 0) { setCurrentId(null); return; }
    if (!currentId || !orgs.find((o) => o.id === currentId)) {
      const id = orgs[0].id;
      setCurrentId(id);
      localStorage.setItem(STORAGE_KEY, id);
    }
  }, [user, orgs, currentId]);

  const setCurrent = useCallback((id: string) => {
    setCurrentId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const createOrg = useCallback<OrgState['createOrg']>(async (input) => {
    if (!user) return { error: 'Not signed in', org: null };
    const payload: any = {
      name: input.name,
      legal_name: input.legal_name ?? input.name,
      trading_as: input.trading_as ?? null,
      reg_number: input.reg_number ?? null,
      vat_number: input.vat_number ?? null,
      paye_number: input.paye_number ?? null,
      uif_number: input.uif_number ?? null,
      is_vat_registered: input.is_vat_registered ?? false,
      vat_period: input.vat_period ?? 'two_monthly',
      vat_basis: input.vat_basis ?? 'invoice',
      financial_year_start_month: input.financial_year_start_month ?? 3,
      base_currency: input.base_currency ?? 'ZAR',
      country: input.country ?? 'ZA',
      timezone: input.timezone ?? 'Africa/Johannesburg',
      address: input.address ?? null,
      city: input.city ?? null,
      province: input.province ?? null,
      postal_code: input.postal_code ?? null,
      phone: input.phone ?? null,
      email: input.email ?? user.email ?? null,
      owner_id: user.id,
    };
    const { data, error } = await supabase
      .from('dodobooks_organizations')
      .insert(payload)
      .select('*')
      .single();
    if (error) return { error: error.message, org: null };
    await refresh();
    if (data?.id) {
      setCurrentId(data.id);
      localStorage.setItem(STORAGE_KEY, data.id);
    }
    return { error: null, org: data as Organization };
  }, [user, refresh]);

  const value: OrgState = {
    orgs,
    currentOrg: orgs.find((o) => o.id === currentId) ?? null,
    loading,
    isPlatformAdmin,
    refresh,
    setCurrent,
    createOrg,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside <OrgProvider>');
  return ctx;
}
